import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, User, ArrowRight, Eye, EyeOff } from 'lucide-react';
import * as api from '../lib/api';
import HCaptcha from '@hcaptcha/react-hcaptcha';

const HCAPTCHA_SITE_KEY = process.env.REACT_APP_HCAPTCHA_SITE_KEY;

const AuthForm = ({ onClose, onNavigate, initialMode = 'signup' }) => {
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [gender, setGender] = useState(''); // 'brother' or 'sister'
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const captchaRef = useRef(null);

  const resetCaptcha = () => {
    captchaRef.current?.resetCaptcha();
    setCaptchaToken(null);
  };

  const { signIn, signUp, signInWithGoogle } = useAuth();
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) return;
    setResetLoading(true);
    try {
      await api.forgotPassword(resetEmail.trim());
      setResetSent(true);
    } catch {
      setResetSent(true); // Show success regardless for security
    }
    setResetLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isLogin && !gender) {
      setError('Please select whether you are a Brother or Sister');
      return;
    }

    if (HCAPTCHA_SITE_KEY && !captchaToken) {
      setError('Please complete the captcha');
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        await signIn(email, password, captchaToken);
        resetCaptcha();
        onClose?.();
      } else {
        const result = await signUp(email, password, displayName, gender, captchaToken);
        resetCaptcha();
        if (result?.session) {
          onClose?.();
        } else {
          // Email confirmation required — show message instead of closing silently
          setSignupSuccess(true);
        }
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(err.message || 'Google sign in failed');
    }
  };

  return (
    <>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md mx-auto"
    >
      <div className="glass-card rounded-arch-xl p-8 shadow-lg">
        {signupSuccess ? (
          <div className="text-center py-4" data-testid="signup-success">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-7 h-7 text-primary" />
            </div>
            <h2 className="font-heading text-xl font-semibold text-primary mb-2">Check your email</h2>
            <p className="text-text-muted text-sm mb-6">
              We sent a confirmation link to <span className="font-medium text-text-main">{email}</span>.
              Click it to activate your account, then sign in.
            </p>
            <button
              onClick={() => { setSignupSuccess(false); setIsLogin(true); resetCaptcha(); }}
              className="w-full py-3 bg-primary text-primary-foreground rounded-arch font-medium btn-hover"
              data-testid="goto-signin-btn"
            >
              Go to Sign In
            </button>
          </div>
        ) : (
        <>
        <h2 className="font-heading text-2xl font-semibold text-primary mb-2">
          {isLogin ? 'Welcome Back' : 'Join Sirati'}
        </h2>
        <p className="text-text-main mb-6">
          {isLogin ? 'Continue your growth journey' : 'Create your free account, answer a few questions, and explore your personalized Sirati dashboard.'}
        </p>

        {error && (
          <div className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-3 rounded-arch mb-4" data-testid="auth-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
              <input
                type="text"
                placeholder="Display Name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/70 dark:input-white-dark rounded-arch border border-primary/20 focus:ring-2 focus:ring-primary transition-all text-text-main"
                required={!isLogin}
                data-testid="signup-name-input"
              />
            </div>
          )}

          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/70 dark:input-white-dark rounded-arch border border-primary/20 focus:ring-2 focus:ring-primary transition-all text-text-main"
              required
              data-testid="auth-email-input"
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-12 pr-12 py-3 bg-white/70 dark:input-white-dark rounded-arch border border-primary/20 focus:ring-2 focus:ring-primary transition-all text-text-main"
              required
              data-testid="auth-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-main"
              data-testid="toggle-password-visibility"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          {/* Forgot Password Link */}
          {isLogin && (
            <div className="text-right">
              <button type="button" onClick={() => setShowForgotPassword(true)}
                className="text-xs text-primary hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </button>
            </div>
          )}

          {/* Gender Selection - Only for signup */}
          {!isLogin && (
            <div className="pt-2">
              <p className="text-sm font-medium text-text-main mb-3">I am a:</p>
              <div className="flex gap-6">
                <label htmlFor="gender-brother" className="flex items-center gap-2 cursor-pointer">
                  <input
                    id="gender-brother"
                    type="radio"
                    name="gender"
                    value="brother"
                    checked={gender === 'brother'}
                    onChange={(e) => setGender(e.target.value)}
                    className="radio-custom"
                    data-testid="gender-brother"
                  />
                  <span className="text-text-main">Brother</span>
                </label>
                <label htmlFor="gender-sister" className="flex items-center gap-2 cursor-pointer">
                  <input
                    id="gender-sister"
                    type="radio"
                    name="gender"
                    value="sister"
                    checked={gender === 'sister'}
                    onChange={(e) => setGender(e.target.value)}
                    className="radio-custom"
                    data-testid="gender-sister"
                  />
                  <span className="text-text-main">Sister</span>
                </label>
              </div>
            </div>
          )}

          {HCAPTCHA_SITE_KEY && (
            <div className="flex justify-center pt-1" data-testid="hcaptcha-widget">
              <HCaptcha
                sitekey={HCAPTCHA_SITE_KEY}
                onVerify={(token) => setCaptchaToken(token)}
                onExpire={() => setCaptchaToken(null)}
                ref={captchaRef}
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-primary text-primary-foreground rounded-arch font-medium btn-hover flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid={isLogin ? "login-submit-btn" : "signup-submit-btn"}
          >
            {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create My Free Account')}
            <ArrowRight className="w-5 h-5" />
          </button>

          <p className="text-center text-xs text-text-muted leading-relaxed" data-testid="terms-notice">
            {isLogin
              ? 'By signing in or continuing with Google, you agree to our '
              : 'By creating an account or continuing with Google, you confirm you are 18 or older and agree to our '}
            {onNavigate ? (
              <>
                <button type="button" onClick={() => onNavigate('terms')} className="text-primary hover:underline" data-testid="terms-notice-link">
                  Terms &amp; Conditions
                </button>
                {' and '}
                <button type="button" onClick={() => onNavigate('privacy')} className="text-primary hover:underline">
                  Privacy Policy
                </button>
              </>
            ) : 'Terms & Conditions and Privacy Policy'}
            .
          </p>
        </form>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-primary/20"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-transparent text-text-muted">or continue with</span>
          </div>
        </div>

        <button
          onClick={handleGoogleSignIn}
          className="w-full py-3 bg-white/70 dark:bg-white border border-primary/20 rounded-arch font-medium btn-hover flex items-center justify-center gap-3 hover:bg-primary/10 text-text-main"
          data-testid="google-signin-btn"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <p className="text-center mt-6 text-text-muted">
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <button
            onClick={() => {
              setIsLogin(!isLogin);
              setError('');
              setGender('');
              setSignupSuccess(false);
              resetCaptcha();
            }}
            className="text-primary hover:underline font-medium"
            data-testid="toggle-auth-mode"
          >
            {isLogin ? 'Sign Up' : 'Sign In'}
          </button>
        </p>

        {onNavigate && (
          <p className="text-center mt-3 text-xs text-text-muted">
            <button
              type="button"
              onClick={() => onNavigate('terms')}
              className="hover:underline text-text-muted"
              data-testid="terms-link"
            >
              Terms &amp; Conditions
            </button>
            <span aria-hidden="true"> &middot; </span>
            <button
              type="button"
              onClick={() => onNavigate('about')}
              className="hover:underline text-text-muted"
              data-testid="about-link"
            >
              About &amp; Pricing
            </button>
            <span aria-hidden="true"> &middot; </span>
            <button
              type="button"
              onClick={() => onNavigate('privacy')}
              className="hover:underline text-text-muted"
              data-testid="privacy-policy-link"
            >
              Privacy Policy
            </button>
          </p>
        )}
        </>
        )}
      </div>
    </motion.div>

    {/* Forgot Password Modal */}
    {showForgotPassword && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl" data-testid="forgot-password-modal">
          <h3 className="font-heading text-lg font-semibold text-text-main mb-2">Reset Password</h3>
          {resetSent ? (
            <div>
              <p className="text-sm text-text-muted mb-4">Check your email for a password reset link.</p>
              <button onClick={() => { setShowForgotPassword(false); setResetSent(false); setResetEmail(''); }}
                className="px-4 py-2 bg-primary text-white rounded-lg text-sm w-full" data-testid="forgot-done-btn">Done</button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword}>
              <p className="text-sm text-text-muted mb-4">Enter your email and we'll send you a reset link.</p>
              <input type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)}
                placeholder="your@email.com" required autoFocus
                className="w-full px-4 py-3 bg-primary/5 rounded-lg border border-primary/20 mb-4 text-sm"
                data-testid="forgot-email-input" />
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowForgotPassword(false)}
                  className="flex-1 px-4 py-2 text-sm text-text-muted hover:bg-gray-100 rounded-lg">Cancel</button>
                <button type="submit" disabled={resetLoading}
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg text-sm disabled:opacity-50"
                  data-testid="send-reset-btn">{resetLoading ? 'Sending...' : 'Send reset link'}</button>
              </div>
            </form>
          )}
        </div>
      </div>
    )}
    </>
  );
};

export default AuthForm;
